/**
 * LabounModeController — „Labouň mód": fixed overlay s mouchami, které létají po
 * náhodné trajektorii a jdou sklepnout kliknutím. Mountuje se jednou v `App`.
 *
 * - Overlay `pointer-events:none` → nepřekáží ovládání stránky; jednotlivá moucha má
 *   `pointer-events:auto` (klikatelná → zmizí; při `respawn` se po chvíli objeví nová).
 * - Pohyb řídí JEDNA `rAF` smyčka, která imperativně píše `transform` na DOM uzly much
 *   (žádné React re-rendery per frame — re-render jen při sklepnutí/respawnu/změně počtu).
 * - `prefers-reduced-motion` → mód se VŮBEC neaktivuje (žádné mouchy). Statické
 *   mouchy by šly snadno sklepnout = nefér body oproti ostatním.
 *
 * Chování much (mouší dojem, ne lineární drony) — jen RYCHLÉ/krátké pohyby, žádné
 * dlouhé pomalé přelety:
 *   - dart   = krátký RYCHLÝ cuk, často s následným klidem (lezení)
 *   - circle = občas se protočí do kruhu
 *   - escape = „odletí": trochu se zvětší a rovně RYCHLE uletí za okraj — a to SMĚREM,
 *              kterým je natočená (±45°)
 *   - arrive = „přílet": objeví se za okrajem zvětšená a rovně RYCHLE přiletí dovnitř,
 *              přitom se zmenší na normální velikost (zrcadlo escape)
 *
 * Aktivace zrcadlí drunk-mode: admin režim `optIn` + per-user přepínač v hlavičce.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLabounConfig } from "../lib/use-laboun-config.js";
import {
  loadLabounConfig,
  isLabounOptIn,
  incLabounSwatCount,
  takePendingLabounSwats,
  restorePendingLabounSwats,
  FLY_ASSETS,
  LABOUN_CHANGED_EVENT,
} from "../lib/laboun-mode.js";
import { api } from "../lib/api-client.js";
import { useAuth } from "../lib/use-auth.js";

/**
 * Lesklá METALICKÁ hnědo-zelená výplň much. SVG moucha je jen silueta (jeden path,
 * `fill="currentColor"`), proto ji nelze obarvit jako `<img>`. Použijeme SVG jako
 * CSS `mask` a pod ni dáme gradient. KRUHOVÝ (radiální) kovový přechod: světlý
 * střed → tmavá zeleň k okrajům. Je rotačně souměrný, takže působí stejně bez
 * ohledu na natočení mouchy (desaturovaná zeleň, ne žlutá).
 */
const FLY_GRADIENT =
  //"radial-gradient(circle at 44% 35%, #69a74a 0%, #909c5bba 1%, #4b5926ec 3%, #243112 30%, #2b2b2b 60%, #242424 100%)";
  //"radial-gradient(circle at 50% 28%, #111111 0%, #242424 50%, #242424 100%)"
  // "radial-gradient(circle at 50% 58%, #121218 0%, #121216 5%, #141418 20%, #121212 30%, #121212 100%)"
  // "radial-gradient(circle at 50% 58%, #121218 0%, #000000 5%, #000000 20%, #121212 100%)"
  "radial-gradient(circle at 50% 58%, #121218 0%, #121216 5%, #000000 20%, #121212 100%)"
  

interface Fly {
  id: number;
  asset: string;
  size: number;
}

type FlyBehavior = "dart" | "escape" | "arrive";

interface Motion {
  x: number;
  y: number,
  px: number; // předchozí pozice (pro rotaci dle skutečného směru)
  py: number;
  scale: number; // 1 = normál; při escape naroste
  rot: number; // deg
  el: HTMLElement | null; // vnější vrstva — jen translate (nese FIXNÍ stín)
  inner: HTMLElement | null; // vnitřní vrstva — rotate+scale (maskovaná moucha)
  kind: "move" | "circle" | "pause";
  behavior: FlyBehavior;
  /** Odlet při vypnutí módu — po dokončení escape se moucha odebere (nerespawnuje). */
  exiting: boolean;
  // move (po oblouku z [fromX,fromY] do [tx,ty])
  fromX: number;
  fromY: number;
  tx: number;
  ty: number;
  dist: number;
  speed: number; // px/s
  progress: number; // 0..1
  curveAmp: number; // amplituda kolmého oblouku (px)
  curveSign: number; // +1 / -1
  // circle
  cx: number;
  cy: number;
  radius: number;
  angle: number;
  angVel: number; // rad/s
  swept: number; // nasčítaný |úhel|
  turns: number; // kolik radiánů celkem opsat
  // pause
  pauseUntil: number;
}

const vw = () => (typeof window !== "undefined" ? window.innerWidth : 1024);
const vh = () => (typeof window !== "undefined" ? window.innerHeight : 768);
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/**
 * O kolik se moucha „zvedne" (přírůstek scale) v nejvyšším bodě odletu/příletu.
 * 0 = u podložky (běžný pohyb), peak = 1 + LIFT_AMOUNT. Stejná konstanta řídí i
 * velikost vnějšího (neotáčejícího se) stínu — viz výpočet `lift` v rAF ticku.
 */
const LIFT_AMOUNT = 0.6;

/** Nastaví „move" akci z aktuální pozice do cíle. allowOffscreen = nechá cíl za okrajem. */
function setMove(
  m: Motion,
  tx: number,
  ty: number,
  speed: number,
  behavior: FlyBehavior,
  curveAmp: number,
  allowOffscreen = false,
): void {
  const W = vw();
  const H = vh();
  m.kind = "move";
  m.behavior = behavior;
  m.fromX = m.x;
  m.fromY = m.y;
  m.tx = allowOffscreen ? tx : clamp(tx, 8, W - 8);
  m.ty = allowOffscreen ? ty : clamp(ty, 8, H - 8);
  m.dist = Math.max(1, Math.hypot(m.tx - m.fromX, m.ty - m.fromY));
  m.speed = speed;
  m.progress = 0;
  m.curveAmp = curveAmp;
  m.curveSign = Math.random() < 0.5 ? -1 : 1;
}

/**
 * „Odletí": trochu se zvětší a rovně RYCHLE uletí za okraj — SMĚREM, kterým je moucha
 * natočená (±45°). Heading = m.rot - 90 (transform má rotate = směr pohybu + 90°).
 */
function planEscape(m: Motion): void {
  const W = vw();
  const H = vh();
  const heading = ((m.rot - 90) * Math.PI) / 180;
  const ang = heading + (Math.random() - 0.5) * (Math.PI / 2); // ±45°
  const far = Math.hypot(W, H) + 120; // určitě až za okraj
  const tx = m.x + Math.cos(ang) * far;
  const ty = m.y + Math.sin(ang) * far;
  setMove(m, tx, ty, 620 + Math.random() * 380, "escape", 0, true);
}

/**
 * „Odlet při vypnutí": zamíří k NEJBLIŽŠÍMU okraji (rotace ho otočí tím směrem),
 * trochu se zvětší a rovně rychle uletí za okraj. Po dokončení se moucha odebere
 * (`exiting`), nerespawnuje se.
 */
function planExitEscape(m: Motion): void {
  const W = vw();
  const H = vh();
  const far = Math.hypot(W, H) + 120;
  const dl = m.x; // vzdálenost k levému okraji
  const dr = W - m.x;
  const dt = m.y;
  const db = H - m.y;
  const min = Math.min(dl, dr, dt, db);
  let tx = m.x;
  let ty = m.y;
  if (min === dl) tx = -far;
  else if (min === dr) tx = W + far;
  else if (min === dt) ty = -far;
  else ty = H + far;
  m.exiting = true;
  setMove(m, tx, ty, 640 + Math.random() * 360, "escape", 0, true);
}

/** „Přílet": vstup za náhodným okrajem (zvětšená) + rovný RYCHLÝ vlet dovnitř (zrcadlo escape). */
function respawnFromEdge(m: Motion): void {
  const W = vw();
  const H = vh();
  const edge = Math.floor(Math.random() * 4);
  if (edge === 0) {
    m.x = -80;
    m.y = Math.random() * H;
  } else if (edge === 1) {
    m.x = W + 80;
    m.y = Math.random() * H;
  } else if (edge === 2) {
    m.x = Math.random() * W;
    m.y = -80;
  } else {
    m.x = Math.random() * W;
    m.y = H + 80;
  }
  m.px = m.x;
  m.py = m.y;
  m.scale = 1.45; // zmenší se během letu zpět na 1 (řeší tick)
  // cíl ve vnitřní oblasti obrazovky → rovný rychlý přílet zvenku dovnitř
  const tx = W * (0.2 + Math.random() * 0.6);
  const ty = H * (0.2 + Math.random() * 0.6);
  setMove(m, tx, ty, 560 + Math.random() * 320, "arrive", 0, true);
}

/** Delší „klid" na místě (1.5–6 s), během kterého moucha drobně leze a zkoumá okolí. */
function startCrawl(m: Motion, now: number): void {
  m.kind = "pause";
  m.pauseUntil = now + 1500 + Math.random() * 4500; // 1.5–6 s
  // první mikrocíl těsně vedle (pár px) → pomalé lezení
  const a = Math.random() * Math.PI * 2;
  const r = 3 + Math.random() * 12;
  m.tx = m.x + Math.cos(a) * r;
  m.ty = m.y + Math.sin(a) * r;
}

/** Naplánuje další akci (po dokončení té předchozí). Váhy = mouší dojem. */
function planAction(m: Motion): void {
  const W = vw();
  const H = vh();
  const roll = Math.random();
  if (roll < 0.1) {
    // protočit do kruhu kolem aktuálního bodu
    m.kind = "circle";
    m.radius = 16 + Math.random() * 46;
    const a0 = Math.random() * Math.PI * 2;
    m.angle = a0;
    m.cx = m.x - Math.cos(a0) * m.radius;
    m.cy = m.y - Math.sin(a0) * m.radius;
    m.angVel = (Math.random() < 0.5 ? -1 : 1) * (2 + Math.random() * 2.5);
    m.turns = Math.PI * 2 * (1 + Math.random()); // 1–2 otáčky
    m.swept = 0;
    return;
  }
  if (roll < 0.17) {
    planEscape(m);
    return;
  }
  // dart — krátký/střední RYCHLÝ cuk, malý oblouk. Žádné pomalé dlouhé přelety.
  const ang = Math.random() * Math.PI * 2;
  const d = 50 + Math.random() * 200; // 50–250 px
  const tx = clamp(m.x + Math.cos(ang) * d, 8, W - 8);
  const ty = clamp(m.y + Math.sin(ang) * d, 8, H - 8);
  setMove(m, tx, ty, 280 + Math.random() * 240, "dart", 3 + Math.random() * 9); // 280–520 px/s
  // Krátký klid (lezení) po cuku řeší tick po dokončení dartu.
}

export function LabounModeController() {
  const config = useLabounConfig();
  const [optIn, setOptIn] = useState(isLabounOptIn);
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
  );

  // Načti admin config + sleduj per-user opt-in a OS preferenci.
  useEffect(() => {
    void loadLabounConfig();
  }, []);
  useEffect(() => {
    const onChange = () => setOptIn(isLabounOptIn());
    window.addEventListener(LABOUN_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(LABOUN_CHANGED_EVENT, onChange);
  }, []);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Perzistence počtu sklepnutých much do profilu PŘIHLÁŠENÉHO hráče. Kliknutí
  // se stackují na klientu (`pending`) a sem se flushují jako delta — periodicky,
  // při skrytí/odchodu stránky a při odhlášení/unmountu. Nepřihlášeným se počet
  // jen sčítá v session (nemáme kam ho uložit).
  const { isLoggedIn } = useAuth();
  useEffect(() => {
    if (!isLoggedIn) return;
    let flushing = false;
    const flush = () => {
      if (flushing) return;
      const delta = takePendingLabounSwats();
      if (delta <= 0) return;
      flushing = true;
      void api
        .post("/players/me/laboun-swats", { delta })
        .catch(() => restorePendingLabounSwats(delta)) // offline/chyba → zkus příště
        .finally(() => {
          flushing = false;
        });
    };
    const interval = window.setInterval(flush, 15_000);
    const onHidden = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", flush);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", flush);
      flush(); // poslední flush při odhlášení / unmountu
    };
  }, [isLoggedIn]);

  // Mouchy NEpobíhají na vlastní kartě (/me) ani v administraci (/admin/*) —
  // tam by jen překážely při práci. `active=false` rozjede úklid (vyprázdní
  // mouchy + zastaví rAF) přes existující efekty níže.
  const { pathname } = useLocation();
  const routeBlocked = pathname === "/me" || pathname.startsWith("/admin");

  // `prefers-reduced-motion` → mód se VŮBEC neaktivuje. Statické (neletící) mouchy
  // by šly triviálně sklepnout = nefér farmení bodů oproti ostatním. Raději nic.
  const active = config.mode === "optIn" && optIn && !routeBlocked && !reducedMotion;
  const activeRef = useRef(active);
  activeRef.current = active;

  const [flies, setFlies] = useState<Fly[]>([]);
  // `exiting` = mód se právě vypnul a mouchy odlétají (rAF musí dál běžet, než zmizí).
  const [exiting, setExiting] = useState(false);
  const motion = useRef<Map<number, Motion>>(new Map());
  const idSeq = useRef(0);
  const wasActive = useRef(false);

  const spawn = useCallback((): Fly => {
    const id = idSeq.current++;
    const asset = FLY_ASSETS[Math.floor(Math.random() * FLY_ASSETS.length)]!;
    const size = 22 + Math.floor(Math.random() * 22); // 22–44 px
    const x = Math.random() * vw();
    const y = Math.random() * vh();
    const m: Motion = {
      x,
      y,
      px: x,
      py: y,
      scale: 1,
      rot: 0,
      el: null,
      inner: null,
      kind: "move",
      behavior: "dart",
      fromX: x,
      fromY: y,
      tx: x,
      ty: y,
      dist: 1,
      speed: 80,
      progress: 1,
      curveAmp: 0,
      curveSign: 1,
      cx: x,
      cy: y,
      radius: 0,
      angle: 0,
      angVel: 0,
      swept: 0,
      turns: 0,
      pauseUntil: 0,
      exiting: false,
    };
    planAction(m);
    motion.current.set(id, m);
    return { id, asset, size };
  }, []);

  // Životní cyklus much:
  //  - ZAPNUTÍ: mouchy se NEzobrazí najednou — postupně PŘILETÍ z okraje v náhodných
  //    rozestupech (0–5 s od zapnutí / od příletu poslední). Reduced-motion = statické.
  //  - ZMĚNA flyCount za běhu: dorovná počet (staggered přílet) / ořízne přebytek.
  //  - VYPNUTÍ: mouchy odletí k nejbližšímu okraji (`exiting`); rAF je odnese a odebere.
  useEffect(() => {
    const prev = wasActive.current;
    wasActive.current = active;
    let cancelled = false;
    const timers: number[] = [];

    if (active) {
      setExiting(false);
      // Čerstvé zapnutí → začni načisto (zahoď případné dolétávající z předchozího cyklu).
      if (!prev) {
        motion.current.clear();
        setFlies([]);
      }
      // Ořízni přebytek (snížení flyCount za běhu).
      setFlies((cur) => {
        if (cur.length <= config.flyCount) return cur;
        for (const f of cur.slice(config.flyCount)) motion.current.delete(f.id);
        return cur.slice(0, config.flyCount);
      });
      // Doplň chybějící mouchy se staggered příletem.
      const need = config.flyCount - (prev ? motion.current.size : 0);
      let delay = 0;
      for (let i = 0; i < need; i++) {
        delay += Math.random() * 5000; // 0–5 s od předchozího příletu
        timers.push(
          window.setTimeout(() => {
            if (cancelled) return;
            setFlies((cur) => {
              if (cur.length >= config.flyCount) return cur;
              const f = spawn();
              // Rychlý přílet z okraje (mimo reduced-motion, kde se jen staticky objeví).
              if (!reducedMotion) {
                const m = motion.current.get(f.id);
                if (m) respawnFromEdge(m);
              }
              return [...cur, f];
            });
          }, delay),
        );
      }
    } else if (prev) {
      // Právě se vyplo.
      if (reducedMotion || motion.current.size === 0) {
        motion.current.clear();
        setFlies([]);
        setExiting(false);
      } else {
        setExiting(true);
        motion.current.forEach((m) => planExitEscape(m)); // odletí k nejbližšímu okraji
      }
    }

    return () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [active, config.flyCount, reducedMotion, spawn]);

  // rAF pohyb (jen aktivní + bez reduced-motion). Čte/píše `motion` ref → nové mouchy
  // (respawn) se zapojí samy, smyčku není třeba restartovat.
  useEffect(() => {
    if ((!active && !exiting) || reducedMotion) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const toRemove: number[] = []; // dolétlé „exiting" mouchy (po vypnutí)
      for (const [id, m] of motion.current) {
        if (!m.el) continue;

        if (m.kind === "pause") {
          // „Klid" = drobné průzkumné lezení: pomalu k mikrocíli, po dosažení občas
          // nový mikrocíl poblíž, nebo se jen pootočí (rozhlíží se).
          const cdx = m.tx - m.x;
          const cdy = m.ty - m.y;
          const cd = Math.hypot(cdx, cdy);
          if (cd > 0.5) {
            const step = Math.min(cd, 110 * dt); // rychlý cupot k mikrocíli (krátká vzdálenost)
            m.x += (cdx / cd) * step;
            m.y += (cdy / cd) * step;
          } else if (Math.random() < 0.03) {
            if (Math.random() < 0.55) {
              const a = Math.random() * Math.PI * 2;
              const r = 3 + Math.random() * 14;
              m.tx = m.x + Math.cos(a) * r;
              m.ty = m.y + Math.sin(a) * r;
            } else {
              m.rot += (Math.random() - 0.5) * 55; // rozhlédne se na místě
            }
          }
          if (now >= m.pauseUntil) planAction(m);
        } else if (m.kind === "circle") {
          m.angle += m.angVel * dt;
          m.swept += Math.abs(m.angVel) * dt;
          m.x = m.cx + Math.cos(m.angle) * m.radius;
          m.y = m.cy + Math.sin(m.angle) * m.radius;
          if (m.swept >= m.turns) planAction(m);
        } else {
          // move (po oblouku, krátké rychlé cuky)
          m.progress += (m.speed * dt) / m.dist;
          const t = Math.min(1, m.progress);
          let bx = m.fromX + (m.tx - m.fromX) * t;
          let by = m.fromY + (m.ty - m.fromY) * t;
          if (m.curveAmp > 0) {
            // kolmá výchylka (oblouk) — vrchol uprostřed cesty
            const ux = (m.ty - m.fromY) / m.dist;
            const uy = -(m.tx - m.fromX) / m.dist;
            const off = Math.sin(t * Math.PI) * m.curveAmp * m.curveSign;
            bx += ux * off;
            by += uy * off;
          }
          m.x = bx;
          m.y = by;
          if (m.behavior === "escape") m.scale = 1 + LIFT_AMOUNT * t; // vystoupá
          else if (m.behavior === "arrive") m.scale = 1 + LIFT_AMOUNT * (1 - t); // klesá k podložce
          if (t >= 1) {
            if (m.behavior === "escape") {
              if (m.exiting) {
                toRemove.push(id); // odlet při vypnutí → mouchu odeber, nerespawnuj
                continue;
              }
              respawnFromEdge(m);
            } else if (m.behavior === "dart" && Math.random() < 0.7) {
              startCrawl(m, now); // po cuku se obvykle zastaví a zkoumá
            } else {
              planAction(m);
            }
          }
        }

        // rotace dle skutečného směru pohybu (plynule kopíruje oblouk/kruh)
        const mdx = m.x - m.px;
        const mdy = m.y - m.py;
        if (mdx * mdx + mdy * mdy > 0.02) {
          m.rot = (Math.atan2(mdy, mdx) * 180) / Math.PI + 90;
          m.px = m.x;
          m.py = m.y;
        }
        // Vnější vrstva = jen POSUN (nese fixní, neotáčející se stín).
        m.el.style.transform = `translate(${m.x}px, ${m.y}px)`;
        // Stín reaguje na „výšku" mouchy: při odletu/příletu (m.scale > 1) se
        // odpoutá od podložky → větší offset i rozostření a světlejší (difúznější)
        // stín. U podložky (lift = 0) je to přesně původní těsný stín.
        const lift = clamp((m.scale - 1) / LIFT_AMOUNT, 0, 1); // 0 = u podložky, 1 = peak
        const sx = 3 + lift * 11;
        const sy = 5 + lift * 15;
        const blur = 3 + lift * 8;
        const alpha = 0.65 - lift * 0.32;
        m.el.style.filter = `drop-shadow(${sx}px ${sy}px ${blur}px rgba(0,0,0,${alpha}))`;
        // Vnitřní vrstva = rotace + zvětšení (otáčí se i s glow stínem).
        if (m.inner) m.inner.style.transform = `rotate(${m.rot}deg) scale(${m.scale})`;
      }
      // Odeber dolétlé mouchy (odlet po vypnutí). Když po vypnutí zmizí všechny → konec.
      if (toRemove.length > 0) {
        for (const id of toRemove) motion.current.delete(id);
        setFlies((prev) => prev.filter((f) => !toRemove.includes(f.id)));
        if (!active && motion.current.size === 0) setExiting(false);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, exiting, reducedMotion]);

  // Hover-escape: necháš-li kurzor 2 s nad mouchou, uletí (planEscape). Timery per fly.
  const hoverTimers = useRef<Map<number, number>>(new Map());
  const clearHover = useCallback((id: number) => {
    const t = hoverTimers.current.get(id);
    if (t !== undefined) {
      window.clearTimeout(t);
      hoverTimers.current.delete(id);
    }
  }, []);
  const armHover = useCallback(
    (id: number) => {
      clearHover(id);
      const t = window.setTimeout(() => {
        hoverTimers.current.delete(id);
        const m = motion.current.get(id);
        if (m) planEscape(m); // moucha uletí (rAF ji odnese za okraj, pak respawn)
      }, 500);
      hoverTimers.current.set(id, t);
    },
    [clearHover],
  );
  // Úklid všech hover timerů při unmountu.
  useEffect(() => {
    const timers = hoverTimers.current;
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      timers.clear();
    };
  }, []);

  // Pošle do úletu (planEscape) všechny mouchy v okruhu `radius` kolem bodu [x,y].
  // Sdílí sklepnutí (okolní mouchy se rozletí) i scroll/touch (vyplašení pohybem).
  const escapeNear = useCallback((x: number, y: number, radius: number) => {
    motion.current.forEach((m) => {
      if (m.exiting || m.behavior === "escape") return;
      if (Math.hypot(m.x - x, m.y - y) <= radius) planEscape(m);
    });
  }, []);

  const swat = useCallback(
    (id: number) => {
      clearHover(id);
      const hit = motion.current.get(id);
      const hx = hit?.x;
      const hy = hit?.y;
      motion.current.delete(id);
      incLabounSwatCount();
      // Haptická odezva při sklepnutí. POZOR: Web Vibration API podporuje pouze
      // Android (Chrome/Edge/Firefox) — iOS Safari ani iOS PWA ho NEMAJÍ (na iPhonu
      // nelze z webu vibrovat vůbec) a desktop nemá motor. Voláme synchronně uvnitř
      // klik-gesta (jinak Chrome volání zahodí). 40 ms = krátké, ale citelné.
      try {
        if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
          navigator.vibrate(40);
        }
      } catch {
        // ignore (mimo secure context apod.)
      }
      setFlies((prev) => prev.filter((f) => f.id !== id));
      // Okolní mouchy se sklepnutí leknou a odletí.
      if (hx !== undefined && hy !== undefined) escapeNear(hx, hy, 160);
      if (config.respawn) {
        window.setTimeout(() => {
          if (!activeRef.current) return; // mezitím se vyplo/odešlo → nevracej mouchu
          setFlies((prev) => (prev.length < config.flyCount ? [...prev, spawn()] : prev));
        }, 800 + Math.random() * 2500);
      }
    },
    [config.respawn, config.flyCount, spawn, clearHover, escapeNear],
  );

  // Scroll / touch-move = vyplašení much: mouchy poblíž kurzoru (při scrollu) nebo
  // poblíž bodu touch-move (na mobilu) odletí. Sleduje poslední pozici myši.
  useEffect(() => {
    if (!active || reducedMotion) return;
    let lastMouseX = -9999;
    let lastMouseY = -9999;
    const onMouseMove = (e: MouseEvent) => {
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
    };
    const onScroll = () => escapeNear(lastMouseX, lastMouseY, 90);
    const onTouchMove = (e: TouchEvent) => {
      for (const t of Array.from(e.touches)) escapeNear(t.clientX, t.clientY, 90);
    };
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [active, reducedMotion, escapeNear]);

  if ((!active && !exiting) || flies.length === 0) return null;

  return (
    <div aria-hidden className="fixed inset-0 z-[60] overflow-hidden pointer-events-none">
      {flies.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => swat(f.id)}
          onMouseEnter={() => armHover(f.id)}
          onMouseLeave={() => clearHover(f.id)}
          ref={(el) => {
            const m = motion.current.get(f.id);
            if (!m) return;
            m.el = el;
            // Počáteční pozice hned (bez bliknutí v levém horním rohu). Jen POSUN.
            if (el) el.style.transform = `translate(${m.x}px, ${m.y}px)`;
          }}
          className="bw-fly absolute top-0 left-0 pointer-events-auto"
          // FIXNÍ stín — na vnější (jen translate) vrstvě, takže se NEOTÁČÍ s mouchou
          // → moucha vrhá stín vždy stejným směrem (světlo „shora-zleva").
          style={{ width: f.size, height: f.size, filter: "drop-shadow(3px 5px 3px rgba(0,0,0,0.65))" }}
          aria-label="Sklepnout mouchu"
        >
          {/* Vnitřní vrstva — rotace + zvětšení. Nese glow stín kopírující tvar mouchy
              (drop-shadow MUSÍ být na rodiči masky, jinak ho maska ořízne). Tenhle se
              otáčí spolu s mouchou. */}
          <div
            aria-hidden
            ref={(el) => {
              const m = motion.current.get(f.id);
              if (!m) return;
              m.inner = el;
              if (el) el.style.transform = `rotate(${m.rot}deg) scale(${m.scale})`;
            }}
            className="block w-full h-full"
            style={{ filter: "drop-shadow(0 0 1px rgba(127,127,127,0.75))" }}
          >
            <span
              aria-hidden
              className="block w-full h-full select-none pointer-events-none"
              style={{
                background: FLY_GRADIENT,
                WebkitMaskImage: `url("${f.asset}")`,
                maskImage: `url("${f.asset}")`,
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
                WebkitMaskSize: "contain",
                maskSize: "contain",
              }}
            />
          </div>
        </button>
      ))}
    </div>
  );
}
